import React, { useState, useEffect } from 'react';
import { updateEvent, updateEventImage, deleteEventImage, deleteEvent } from '../services/eventsService';
import { getDisplayErrorMessage } from '../services/apiError';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CloseButton from './CloseButton';
import RichTextEditor from './RichTextEditor';
import ErrorMessage from './ErrorMessage';
import { MAX_IMAGE_SIZE_LABEL, validateImageSize, isRealDate } from './constants';
import DateInput from './DateInput';

const EditEventForm = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [fullDescription, setFullDescription] = useState('');
  const { eventId } = useParams();
  const location = useLocation();
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [image, setImage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [imageDeleted, setImageDeleted] = useState(false);
  const navigate = useNavigate();
  const [initialEventData, setInitialEventData] = useState(null);
  const [errors, setErrors] = useState({
    name: '',
    shortDescription: '',
    fullDescription: '',
    startDate: '',
    endDate: '',
    image: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    setError(null);
    setErrors({});
    setSuccessMessage(null);

    const validationErrors = {};

    if (name.trim() === "") {
      validationErrors.name = "Name is required";
    }

    const startDateTimeStr = `${startDate}T${startTime || '00:00:00'}Z`;
    const startDateObj = new Date(startDateTimeStr);

    if (startDate.trim() !== "" && (isNaN(startDateObj.getTime()) || !isRealDate(startDate))) {
      validationErrors.startDate = "Invalid start date";
    }

    if (endDate.trim() !== "") {
      const endDateTimeStr = `${endDate}T${endTime || '00:00:00'}Z`;
      const endDateObj = new Date(endDateTimeStr);
      if (isNaN(endDateObj.getTime()) || !isRealDate(endDate)) {
        validationErrors.endDate = "Invalid end date";
      } else if (!isNaN(startDateObj.getTime()) && startDateObj.getTime() > endDateObj.getTime()) {
        validationErrors.endDate = "End date must be later than start date";
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const startDateTs = Math.floor(startDateObj.getTime() / 1000);
      
      let endDateTs = null;
      if (endDate.trim() !== '') {
        const endDateTimeStr = `${endDate}T${endTime || '00:00:00'}Z`;
        endDateTs = Math.floor(new Date(endDateTimeStr).getTime() / 1000);
      }

      if (hasChanges) {
        const eventData = {
          name,
          shortDescription: shortDescription,
          fullDescription: fullDescription,
          startDateTs: startDateTs,
          endDateTs: endDateTs
        };
        await updateEvent(eventId, eventData);
      }

      if (imageDeleted) {
        await deleteEventImage(eventId);
      }

      if (image) {
        await updateEventImage(eventId, image);
      }

      if (hasChanges) {
        setSuccessMessage("Event successfully updated!");
      }
      navigate(-1);
    } catch (err) {
      setError(getDisplayErrorMessage(err));
    }
    finally {
      setIsLoading(false);
    }
  };
  
  const validateField = (name, value) => {
    let error = "";
    
    switch (name) {
      case "name":
        if (value.trim() === "") {
          error = "Name is required";
        }
        break;
      case "startDate":
        if (value.trim() !== "" && !isRealDate(value)) {
          error = "Invalid date";
        }
        break;
      case "endDate":
        if (value.trim() !== '' && !isRealDate(value)) {
          error = "Invalid date";
        } else if (startDate.trim() !== '' && value.trim() !== '' && isRealDate(startDate) && isRealDate(value)) {
          const sTime = new Date(`${startDate}T${startTime || '00:00:00'}Z`).getTime();
          const eTime = new Date(`${value}T${endTime || '00:00:00'}Z`).getTime();
          if (sTime > eTime) {
            error = "End date must be after start date";
          }
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    if (file) {
      if (validateImageSize(file, setErrors, setImage, setImagePreview)) return;
      setImage(file);
      setImageDeleted(false);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      setImage(null);
      setImagePreview(null);
    }
  };

  const handlePaste = (e) => {
    if (!isHoveringButton) {
      return;
    }
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();

        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }

        if (file) {
          if (validateImageSize(file, setErrors, setImage, setImagePreview)) {
            e.preventDefault();
            break;
          }
          setImage(file);
          setImageDeleted(false);
          const previewUrl = URL.createObjectURL(file);
          setImagePreview(previewUrl);
        }
        e.preventDefault();
        break;
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      const file = files[0];

      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      if (file) {
        if (file.type.startsWith('image/')) {
          if (validateImageSize(file, setErrors, setImage, setImagePreview)) return;
          setImage(file);
          setImageDeleted(false);
          const previewUrl = URL.createObjectURL(file);
          setImagePreview(previewUrl);
        } else {
          setErrors(prev => ({ ...prev, image: 'Only image files are allowed' }));
        }
      }
    }
  }

  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImage(null);
    setImagePreview(null);
    setImageDeleted(true);
    setErrors(prev => ({ ...prev, image: '' }));
  };

  const handleDelete = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await deleteEvent(eventId);
      const allEvents = location.state?.allEvents;
      const historyId = location.state?.event?.historyId;

      if (allEvents && historyId) {
        const deletedIndex = allEvents.findIndex(e => String(e.id) === String(eventId));

        if (deletedIndex !== -1) {
          const newEvents = allEvents.filter(e => String(e.id) !== String(eventId));
          const newSelectedEvent = newEvents.length > 0 ? newEvents[Math.max(0, deletedIndex - 1)] : null;

          const searchParams = new URLSearchParams();
          searchParams.set('historyId', historyId);
          if (newSelectedEvent) {
            searchParams.set('eventId', newSelectedEvent.id);
          }

          navigate(`/?${searchParams.toString()}`, { replace: true });
          return;
        }
      }
      navigate(-1);
    } catch (err) {
      setError(getDisplayErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (location.state && location.state.event) {
      const { event } = location.state;
      const startDateIso = event.startDateTs ? new Date(event.startDateTs * 1000).toISOString() : null;
      const endDateIso = event.endDateTs ? new Date(event.endDateTs * 1000).toISOString() : null;

      setInitialEventData({
        name: event.name,
        shortDescription: event.shortDescription,
        fullDescription: event.fullDescription,
        startDateTs: event.startDateTs,
        endDateTs: event.endDateTs,
        historyId: event.historyId,
        startDate: startDateIso ? startDateIso.split('T')[0] : '',
        startTime: startDateIso ? startDateIso.split('T')[1].slice(0, 8) : '',
        endDate: endDateIso ? endDateIso.split('T')[0] : '',
        endTime: endDateIso ? endDateIso.split('T')[1].slice(0, 8) : '',
        hadImage: !!event.image,
      });

      setName(event.name);
      setShortDescription(event.shortDescription);
      setFullDescription(event.fullDescription);
      
      if (startDateIso) {
        setStartDate(startDateIso.split('T')[0]);
        setStartTime(startDateIso.split('T')[1].slice(0, 8));
      }
      if (endDateIso) {
        setEndDate(endDateIso.split('T')[0]);
        setEndTime(endDateIso.split('T')[1].slice(0, 8));
      }
      setImagePreview(event.image);
    }
  }, [location.state]);

  const hasChanges = !!initialEventData && (
    initialEventData.name !== name ||
    initialEventData.shortDescription !== shortDescription ||
    initialEventData.fullDescription !== fullDescription ||
    initialEventData.startDate !== startDate ||
    initialEventData.startTime !== startTime ||
    initialEventData.endDate !== endDate ||
    initialEventData.endTime !== endTime ||
    image !== null ||
    (imageDeleted && initialEventData.hadImage)
  );

  return (
    <div className="h-[calc(100vh-64px)] overflow-y-auto flex items-start justify-center bg-gray-100 py-8">
      <div className="bg-gray-200 mx-auto w-full max-w-4xl rounded shadow-md py-6 relative">
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
        {successMessage && (
          <div 
            className="w-full bg-green-100 text-green-500 text-center absolute shadow-2xl p-4 py-8 border-green-400 border-[1px] rounded-xl top-10 right-auto left-auto hover:cursor-pointer"
            onClick={ () => setSuccessMessage(null)}
          >
          {successMessage}
          </div>
        )}

        <div className="flex justify-between px-4 items-center">
          <button
            onClick={ () =>  setIsModalOpen(true)}
            className="bg-red-500 w-11 h-11 rounded-full z-50 flex items-center justify-center shadow-lg hover:bg-red-600 hover:border-2 hover:border-red-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="white" className="size-7">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
          <CloseButton onClick={() => navigate(-1)} disabled={isLoading}/>
        </div>
        <form
          className="bg-gray-300 rounded px-32 pt-6 pb-8 my-4 flex flex-col shadow-lg" onSubmit={ handleSubmit } onPaste={handlePaste}
        >
          <h1 className="text-center text-3xl mb-6 bg-gray-200 border-2 border-gray-400 rounded-xl py-3">Editing event</h1>
          <div className="mb-6 relative">
          <span className="absolute -left-5 top-1 text-xl text-red-500 font-bold">*</span>
          <input 
            placeholder="Enter a name for event" 
            type="text" 
            className="w-full rounded p-1" 
            value={ name } 
            onChange={ (e) => setName(e.target.value) }
            onBlur={ (e) => validateField('name', e.target.value)}
          />
          {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
          </div>

          <div className="mb-6">
          <input 
            placeholder="Enter a short decription of the event" 
            type="text" 
            className="w-full rounded p-1" 
            value={ shortDescription } 
            onChange={ (e) => setShortDescription(e.target.value) }
          />
          {errors.shortDescription && <p className="text-red-500 text-xs">{errors.shortDescription}</p>}
          </div>

          <div className="mb-6">
            <RichTextEditor
              content={fullDescription}
              onChange={setFullDescription}
              placeholder="Enter a full description of the event"
            />
          {errors.fullDescription && <p className="text-red-500 text-xs">{errors.fullDescription}</p>}
          </div>

          <div className="mb-6 relative">
            <span className="absolute -left-5 top-1 text-xl text-red-500 font-bold">*</span>
            <div className="flex gap-2">
              <DateInput
                className="flex-1"
                value={startDate}
                onChange={(val) => {
                  const wasEmpty = !startDate;
                  setStartDate(val);
                  if (val && wasEmpty && !startTime) {
                    setStartTime('00:00:00');
                  }
                }}
                onBlur={(val) => validateField('startDate', val)}
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setErrors(prev => ({ ...prev, startDate: '' }));
                  if (!startTime) {
                    setStartTime('00:00:00');
                  }
                }}
              >Today</button>
              <input
                type="time"
                step="1"
                className="rounded w-32 p-1 shrink-0"
                value={ startTime }
                onChange={ (e) => setStartTime(e.target.value) }
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => setStartTime(new Date().toTimeString().split(' ')[0])}
              >Now</button>
            </div>
            {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
          </div>

          <div className="mb-6">
            <div className="flex gap-2">
              <DateInput
                className="flex-1"
                value={endDate}
                onChange={(val) => {
                  const wasEmpty = !endDate;
                  setEndDate(val);
                  if (val && wasEmpty && !endTime) {
                    setEndTime('00:00:00');
                  }
                }}
                onBlur={(val) => validateField('endDate', val)}
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setEndDate(today);
                  setErrors(prev => ({ ...prev, endDate: '' }));
                  if (!endTime) {
                    setEndTime('00:00:00');
                  }
                }}
              >Today</button>
              <input
                type="time"
                step="1"
                className="rounded w-32 p-1 shrink-0"
                value={ endTime }
                onChange={ (e) => setEndTime(e.target.value) }
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => setEndTime(new Date().toTimeString().split(' ')[0])}
              >Now</button>
            </div>
            {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
          </div>

          <div className="mb-6 flex items-center gap-4">
            <label 
              htmlFor="image-upload" 
              className={`${isDragging ? 'border-dashed border-blue-500 bg-blue-200' : ''} flex-1 text-center cursor-pointer bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 disabled:opacity-50 disabled:cursor-not-allowed text400`}
              onMouseEnter={() => setIsHoveringButton(true)}
              onMouseLeave={() => setIsHoveringButton(false)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              Click to upload / Paste from clipboard / Drop image here
              {errors.image
                ? <span className="block text-xs text-red-500 mt-1">{errors.image}</span>
                : <span className="block text-xs text-gray-500 mt-1">Max size: {MAX_IMAGE_SIZE_LABEL}</span>
              }
            </label>
            <input
              id="image-upload"
              type="file" 
              onChange={handleImageChange}
              className="hidden"
              accept="image/*"
            />

            {imagePreview && (
              <div className="w-16 h-16 flex-shrink-0 border-2 border-gray-400 rounded bg-white relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full h-full object-cover" 
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center leading-none hover:bg-red-600 p-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <button
            disabled={ isLoading || !hasChanges }
            type="submit" className="bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-200 disabled:hover:border-gray-200"
          >
          {isLoading ? 'Processing...' : 'Update event'}
          </button>
        </form>
      </div>
            {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow-lg">
            <h3 className="text-xl font-bold mb-4">Confirmation of deletion</h3>
            <p className="mb-4">Are you sure you want to delete this event?</p>
            <button 
              onClick={handleDelete}
              className="bg-red-500 text-white rounded p-2 hover:bg-red-600 border-2 hover:border-red-800"
            >
              Yes, delete
            </button>
            <button 
              className="bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 ml-4"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
        )
      }
    </div>
  )
}

export default EditEventForm;